export interface ThreadReplyDelta {
    baselineTotal: number
    appended: number
    reset: boolean
}

export interface ThreadReplyQuestShape {
    sorted_answers?: Array<{ user_id?: unknown; [key: string]: unknown }>
    sorted_answer_ids?: unknown[]
    __knovAnswerHydrationScope?: 'stream-preview' | 'full'
}

// Opening a feed card can seed the quest cache with a stream preview before the
// full thread request settles. Growth during that upgrade was already present
// when the thread opened, so it establishes the baseline instead of counting
// as replies that arrived while the viewer was away from the bottom.
export function threadReplyBaselineIsReady(quest: ThreadReplyQuestShape | undefined): boolean {
    return quest?.__knovAnswerHydrationScope !== 'stream-preview'
}

// Stream previews may hydrate only a few post objects while retaining the
// complete ordered id list. Prefer ids so opening a preview does not make the
// remaining existing posts look newly appended when full hydration lands.
export function threadAnswerTotal(quest: ThreadReplyQuestShape | undefined): number {
    if (!quest) return 0
    if (Array.isArray(quest.sorted_answer_ids)) return quest.sorted_answer_ids.length
    if (Array.isArray(quest.sorted_answers)) return quest.sorted_answers.length
    return 0
}

// Only exclude the viewer's own posts when every post object is present. A
// stream preview can carry all ids but hydrate just its visible tail; treating
// that partial object list as complete would undercount unknown new replies.
export function threadVisibleNewReplyCount(
    quest: ThreadReplyQuestShape,
    appended: number,
    currentUserId: string,
): number {
    const answers = quest.sorted_answers
    if (!currentUserId || !Array.isArray(answers) || answers.length < threadAnswerTotal(quest)) {
        return appended
    }

    return answers
        .slice(-appended)
        .filter(answer => String(answer?.user_id || '') !== currentUserId).length
}

// The first cache payload establishes what was already present when the thread
// opened. Only later growth while away from the bottom is new to the viewer.
export default function threadReplyDelta(
    baselineTotal: number | null,
    total: number,
    atBottom: boolean,
): ThreadReplyDelta {
    if (baselineTotal == null || atBottom || total < baselineTotal) {
        return { baselineTotal: total, appended: 0, reset: true }
    }

    return { baselineTotal, appended: total - baselineTotal, reset: false }
}
